import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { FileUploadArea, DescriptionInput, ProcessingView, ErrorView, CompleteView } from './components';
import {
  UploadedFile,
  SetupAnalyseRequest,
  TasksAnalyseResponse,
  TasksApplyRequest,
  TasksApplyResponse,
} from './stages/types';
import { TasksResultsView, getInitialTaskSelections, getTasksApplySummary } from './stages/tasks';
import { useSetupDialogStyles } from './setupDialogStyles';

type Step = 'upload' | 'processing' | 'results' | 'applying' | 'complete' | 'error';

interface AISetupDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (data: Record<string, unknown>) => void;
  questionnaireAnswers?: Record<string, unknown>;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export function AISetupDialog({ open, onClose, onComplete, questionnaireAnswers }: AISetupDialogProps) {
  const classes = useSetupDialogStyles();

  const [step, setStep] = useState<Step>('upload');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [description, setDescription] = useState('');
  const [applicationUrl, setApplicationUrl] = useState('');
  const [results, setResults] = useState<TasksAnalyseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [selectedSettings, setSelectedSettings] = useState<Set<string>>(new Set());
  const [selectedProfileFields, setSelectedProfileFields] = useState<Set<string>>(new Set());
  const [selectedScreeningQuestions, setSelectedScreeningQuestions] = useState<Set<string>>(new Set());

  const analyseFetch = useEndpointFetch<SetupAnalyseRequest, TasksAnalyseResponse>('/vm/setup/analyse');
  const applyFetch = useEndpointFetch<TasksApplyRequest, TasksApplyResponse>('/vm/onboarding/apply');

  const handleFilesAdded = useCallback((newFiles: UploadedFile[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleFileRemoved = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_unused, i) => i !== index));
  }, []);

  const handleAnalyse = async () => {
    setStep('processing');
    setError(null);

    try {
      const fileData = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          content: await fileToBase64(f.file),
        }))
      );

      const response = await analyseFetch.run({
        section: 'tasks',
        files: fileData,
        description: description || undefined,
        url: applicationUrl || undefined,
        questionnaireAnswers,
      });

      if (response.ok && response.data?.data) {
        const { data } = response.data;
        setResults(data);
        const selections = getInitialTaskSelections(data);
        setSelectedTasks(selections.tasks);
        setSelectedSettings(selections.settings);
        setSelectedProfileFields(selections.profileFields);
        setSelectedScreeningQuestions(selections.screeningQuestions);
        setStep('results');
      } else {
        const errorMessage = response.data?.message || 'Failed to analyse documents';
        setError(errorMessage);
        setStep('error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFiles([]);
    setDescription('');
    setApplicationUrl('');
    setResults(null);
    setError(null);
    setSelectedTasks(new Set());
    setSelectedSettings(new Set());
    setSelectedProfileFields(new Set());
    setSelectedScreeningQuestions(new Set());
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleApply = async () => {
    if (!results) return;

    setStep('applying');
    try {
      const selectedProfileFieldsData = (results.profileFields || []).filter((f) => selectedProfileFields.has(f.name));

      const response = await applyFetch.run({
        taskIds: Array.from(selectedTasks),
        settingIds: Array.from(selectedSettings),
        profileFields: selectedProfileFieldsData,
      });

      if (response.ok) {
        setStep('complete');
      } else {
        setError(response.data?.message || 'Failed to apply configuration');
        setStep('error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleComplete = () => {
    if (onComplete && results) {
      onComplete({
        analysis: results.analysis,
        tasksSelected: Array.from(selectedTasks),
        settingsSelected: Array.from(selectedSettings),
        profileFieldsSelected: Array.from(selectedProfileFields),
        screeningQuestionsSelected: Array.from(selectedScreeningQuestions),
      });
    }
    handleClose();
  };

  const toggleTask = (taskId: string) => {
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleSetting = (settingId: string) => {
    setSelectedSettings((prev) => {
      const next = new Set(prev);
      if (next.has(settingId)) next.delete(settingId);
      else next.add(settingId);
      return next;
    });
  };

  const toggleProfileField = (fieldName: string) => {
    setSelectedProfileFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  };

  const toggleScreeningQuestion = (label: string) => {
    setSelectedScreeningQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const getSubtitle = () => {
    switch (step) {
      case 'upload':
        return 'Upload your onboarding documents';
      case 'processing':
        return 'Analysing your documents...';
      case 'results':
        return 'Review and select items to apply';
      case 'applying':
        return 'Applying configuration...';
      case 'complete':
        return 'Configuration applied successfully';
      case 'error':
        return 'Something went wrong';
      default:
        return '';
    }
  };

  const canAnalyse = files.length > 0 || description.trim().length > 0 || applicationUrl.trim().length > 0;
  const totalSelected =
    selectedTasks.size + selectedSettings.size + selectedProfileFields.size + selectedScreeningQuestions.size;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={handleClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography className={classes.title}>Tasks & Onboarding Setup</Typography>
        <Typography className={classes.subtitle}>{getSubtitle()}</Typography>
      </Box>

      <DialogContent className={classes.content}>
        {step === 'upload' && (
          <>
            <Typography className={classes.description}>
              Upload your existing onboarding documents (PDFs, Word docs, images of forms) and our AI will analyse them
              to suggest the appropriate task configuration for Volaby.
            </Typography>
            <Box className={classes.uploadSection}>
              <FileUploadArea files={files} onFilesAdded={handleFilesAdded} onFileRemoved={handleFileRemoved} />
            </Box>
            <DescriptionInput
              value={description}
              onChange={setDescription}
              label='Or describe your onboarding process'
              placeholder='Describe your volunteer onboarding requirements, compliance needs, or any specific tasks you need...'
              helperText='You can upload documents, provide a description, or both'
            />
            <Typography className={classes.sectionTitle}>Link to existing application form</Typography>
            <TextField
              value={applicationUrl}
              onChange={(e) => setApplicationUrl(e.target.value)}
              placeholder='https://example.com/volunteer-application'
              helperText='We can analyse your existing online application form to suggest profile fields'
              variant='outlined'
              size='small'
              fullWidth
              type='url'
            />
          </>
        )}

        {step === 'processing' && (
          <ProcessingView title='Analysing your documents...' subtitle='This may take a moment' />
        )}

        {step === 'results' && results && (
          <TasksResultsView
            results={results}
            selectedTasks={selectedTasks}
            selectedSettings={selectedSettings}
            selectedProfileFields={selectedProfileFields}
            selectedScreeningQuestions={selectedScreeningQuestions}
            onToggleTask={toggleTask}
            onToggleSetting={toggleSetting}
            onToggleProfileField={toggleProfileField}
            onToggleScreeningQuestion={toggleScreeningQuestion}
          />
        )}

        {step === 'applying' && (
          <ProcessingView title='Applying configuration...' subtitle='Creating tasks and enabling settings' />
        )}

        {step === 'complete' && (
          <CompleteView
            title='Configuration Applied'
            summary={getTasksApplySummary(
              selectedTasks,
              selectedSettings,
              selectedProfileFields,
              selectedScreeningQuestions
            )}
            linkText='Manage Onboarding Tasks'
            linkHref='/admin/tasks'
          />
        )}

        {step === 'error' && error && <ErrorView message={error} onRetry={handleReset} />}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {step === 'upload' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Cancel
            </Button>
            <Button className={classes.primaryButton} onClick={handleAnalyse} disabled={!canAnalyse}>
              Analyse
            </Button>
          </>
        )}

        {step === 'results' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleReset}>
              Start Over
            </Button>
            <Button className={classes.primaryButton} onClick={handleApply} disabled={totalSelected === 0}>
              Apply Configuration ({totalSelected} items)
            </Button>
          </>
        )}

        {step === 'complete' && (
          <>
            <Box />
            <Button className={classes.primaryButton} onClick={handleComplete}>
              Done
            </Button>
          </>
        )}

        {step === 'error' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Close
            </Button>
            <Button className={classes.primaryButton} onClick={handleReset}>
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
